"use client"

import { BatchMintedDataMoleculeReatom } from "@/app/collections/drops/store/batch-minted-molecule.reatom"
import { QuantityPicker } from "@/app/drop/actions/quantity-picker"
import { DropGridMoleculeReatom } from "@/app/drop/store/drop-grid-molecule.reatom"
import { PriceWrapper } from "@/app/price"
import { useEvmMint } from "@/app/sdk/action-molecules/evm/evm-mint-molecule"
import { useWalletConnect } from "@/app/wallet-connect/hooks"
import type { MinimalDrop } from "@anaconda/core/drop/types"
import { Trans, useLingui } from "@lingui/react/macro"
import {
  Button,
  type ButtonProps,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useMemo, useState } from "react"
import { useConnectStatus } from "../../nft/order/widget/actions/use-connect-status"
import { calculateAvailableToMint } from "./utils"

type Props = Omit<ButtonProps, "onClick" | "disabled"> & {
  withPrice?: boolean
  withQuantityPicker?: boolean
  drop: MinimalDrop
}

const MAX_QUANTITY = 50

/**
 * Grid version of MintWidget - has full eligibility logic, uses batch minted data.
 * Batch data is refetched by parent every 5 seconds.
 * For detail page, use the regular MintWidget with individual refetch.
 */
export const GridMintWidget = reatomComponent<Props>(
  ({
    drop,
    withPrice = false,
    withQuantityPicker = true,
    className,
    ...rest
  }) => {
    const { t } = useLingui()
    const [quantity, setQuantity] = useState(1)

    const {
      currentPhaseAtom,
      isThirdwebDropAtom,
      isMintDisabledForUserAtom,
      isMintDisabledAtom,
      mintNotAvailableMessageAtom,
      dropDisplayPriceAtom,
      userClaimLimitForMintAtom,
    } = useMolecule(DropGridMoleculeReatom)

    // Read minted data from batch (refetched by parent)
    const { getMintedData } = useMolecule(BatchMintedDataMoleculeReatom)
    const mintedData = getMintedData(drop.id)()

    const { isConnected } = useWalletConnect()

    const currentPhase = currentPhaseAtom()
    const isMintDisabledForUser = isMintDisabledForUserAtom()
    const isMintDisabled = isMintDisabledAtom()
    const mintNotAvailableMessage = mintNotAvailableMessageAtom()
    const displayPrice = dropDisplayPriceAtom()
    const userClaimLimit = userClaimLimitForMintAtom()

    const isThirdwebDrop = isThirdwebDropAtom()

    // Calculate available quantity using batch minted data
    const userMintedCountInCurrentPhase = mintedData?.myMints ?? 0
    const availableToMint = calculateAvailableToMint({
      maxMintPerWallet: currentPhase.maxMintPerWallet,
      phaseLimit: currentPhase.maxClaimableSupply,
      totalSupply: drop.totalSupply,
      mintedCount: mintedData?.minted ?? 0,
      userMintedCount: userMintedCountInCurrentPhase,
      maxQuantity: MAX_QUANTITY,
      phaseType: currentPhase.type,
      userClaimLimitInfo: userClaimLimit,
    })

    const disabled = isConnected
      ? isMintDisabled || isMintDisabledForUser
      : isMintDisabled

    const { mutate, isPending } = useEvmMint(drop)

    const promptWalletConnection = useConnectStatus(() => {
      if (isThirdwebDrop) {
        ;(mutate as (variables: { quantity?: number }) => void)({ quantity })
      } else {
        mutate(undefined)
      }
    })

    const mint = useMemo(() => {
      if (!isConnected) return promptWalletConnection
      if (isThirdwebDrop) {
        return () => {
          ;(mutate as (variables: { quantity?: number }) => void)({ quantity })
        }
      }
      return () => {
        mutate(undefined)
      }
    }, [isConnected, promptWalletConnection, mutate, quantity, isThirdwebDrop])

    const amount =
      displayPrice.amount !== "0" ? Number(displayPrice.amount) * quantity : "0"

    const buttonContent = withPrice ? (
      amount !== "0" ? (
        <>
          <Trans>Mint for</Trans>{" "}
          <PriceWrapper
            currency={displayPrice.currency}
            amount={amount.toString()}
            className="[&_.currency-symbol]:text-foregroundInvert/100"
          />
        </>
      ) : (
        <Trans>Free mint</Trans>
      )
    ) : (
      <Trans>Mint</Trans>
    )

    return (
      <div className={cn("flex items-center gap-2", className)}>
        {/* Only the Thirdweb drops support the quantity parameter */}
        {isThirdwebDrop && withQuantityPicker && (
          <QuantityPicker
            defaultValue={1}
            onChange={setQuantity}
            className="w-20 rounded-md border-foreground/10 bg-foregroundInvert/20 backdrop-blur-sm"
            maxValue={availableToMint}
            disabled={isConnected ? disabled : isMintDisabled}
            dataTestId="mint-quantity-picker"
          />
        )}
        <Tooltip>
          <TooltipTrigger
            className={cn(disabled && "cursor-not-allowed")}
            asChild
          >
            <Button
              loading={isPending}
              disabled={disabled}
              onClick={mint}
              {...rest}
              className={cn(
                "mint-button",
                "disabled:pointer-events-auto disabled:hover:transform-none",
              )}
              data-testid="mint-button"
            >
              {buttonContent}
            </Button>
          </TooltipTrigger>
          {mintNotAvailableMessage && isConnected && (
            <TooltipContent>{t(mintNotAvailableMessage)}</TooltipContent>
          )}
        </Tooltip>
      </div>
    )
  },
  "GridMintWidget",
)
